import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkedMillingJobTabs } from "@/components/blanks/linked-milling-jobs";
import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyCard } from "@/components/generic/multi-property-card";

import type { LibraryBlank } from "@/data-interfaces/blanks";
import type { ApiError } from "@/data-interfaces/generics";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import { getLibraryBlanksProperties } from "@/lib/data-remappings/blanks";
import {
	BLANKS_PAGE_URL,
	getAppWrappedPageUrl,
	LIBRARY_BLANK_DETAILS_PAGE_URL,
} from "@/lib/page-routes";
import {
	BL_DISC_LINKED_MILLING_JOBS_URL,
	BL_LIBRARY_BLANK_URL,
} from "@/lib/request-paths";

export const Route = createFileRoute(
	getAppWrappedPageUrl(LIBRARY_BLANK_DETAILS_PAGE_URL),
)({
	component: LibraryBlankDetails,
	validateSearch: (search: LibraryBlank): LibraryBlank => {
		search.insertion_date = new Date(search.insertion_date);
		return search;
	},
});

function LibraryBlankDetails() {
	const [t, _] = useTranslation();

	const navigate = useNavigate();

	const libraryBlankSearchParam: LibraryBlank = Route.useSearch();

	const [libraryBlank, setLibraryBlank] = useState<LibraryBlank | ApiError>(
		libraryBlankSearchParam,
	);

	useMQTTSubscribe(BL_LIBRARY_BLANK_URL, setLibraryBlank);

	useMQTTAutoRefreshPublish(30, BL_LIBRARY_BLANK_URL, {
		job_disc_id: libraryBlankSearchParam.id,
	});

	return (
		<div className="text-white p-8 flex flex-col flex-1 gap-y-4">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type={"button"}
					onClick={() => {
						navigate({
							to: BLANKS_PAGE_URL,
							search: { selectedTab: "library" },
						});
					}}
				>
					{"< "}
					{t("Blanks")}
				</button>
			</h2>

			<div className="flex flex-col gap-y-4">
				<h2 className="text-2xl sm:text-3xl">{t("Selected blank")}</h2>

				<MqttSuspense
					objects={libraryBlank}
					requestUrl={BL_LIBRARY_BLANK_URL}
					parameters={{ job_disc_id: libraryBlankSearchParam.id }}
					genericError="Error retrieving library blank"
				>
					{(libraryBlank) => (
						<MultiPropertyCard
							multiPropertyObject={{
								title: libraryBlank.name,
								properties: getLibraryBlanksProperties(libraryBlank),
							}}
						/>
					)}
				</MqttSuspense>
			</div>

			<div className="flex flex-1 flex-col grow justify-center">
				<div className="text-2xl sm:text-3xl">{t("Associated jobs")}</div>

				<LinkedMillingJobTabs
					requestUrl={BL_DISC_LINKED_MILLING_JOBS_URL}
					parameters={{ job_disc_id: libraryBlankSearchParam.id }}
				/>
			</div>
		</div>
	);
}
